import * as angular from 'angular';
import * as _ from "lodash";
import {IPromise, IQService} from "angular";
import {BringozLoaderService} from "../../commons/services/bringoz-loader.service";
import {PoliciesAlertsService} from "./policies-alerts.service";
import {PoliciesRequestsService} from "./policies-requests.service";

export class PoliciesService {

  private betaVersion: any = this.permissionsConsts.version.beta;

  constructor(private permissionsConsts, private IAMPermissions, private bringozLoaderService: BringozLoaderService, private policiesRequestsService: PoliciesRequestsService,
              private policiesAlertsService: PoliciesAlertsService, private $q: IQService, private $log, private $translate) {
  }

  getRolesWithPermissionsByVersion(roles) {
    const isSuperOwner = this.IAMPermissions.hasRole('super.owner');

    return _.chain(roles)
      .filter((r) => {
        return _.isEqual(r.version, this.betaVersion) ? isSuperOwner : true;
      })
      .map((r) => {
        r.permission = r.id === 'billing.manager' ?
          'organizations.create' :
          (r.id === 'site.reports' ?
            'report.generate.list' : 'iam.policy.get');

        return r;
      })
      .uniq()
      .value();
  }

  getRoleMap(roles) {

    var rolesMap = {};

    _.forEach(roles, (r) => {
      var group = _.get(r, 'module.id', 'core');
      if (!rolesMap[group]) {
        rolesMap[group] = [];
      }
      rolesMap[group].push(r);
    });

    return rolesMap;
  }

  translateRolesMap(rolesMap) {

    const rolesMapCopy = angular.copy(rolesMap);

    _.forOwn(rolesMap, (permissions, key) => {
      rolesMapCopy[key] = _.map(permissions, (role) => {
        _.set(role, 'translation', this.$translate.instant('roles.' + role.id));
        return role;
      })
    })

    return rolesMapCopy;
  }

  addUser(policyId, user, orgId, siteId) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.policiesRequestsService.addNewUser(policyId, user, orgId, siteId)
      .then((addedUser) => {
        deferred.resolve(addedUser);
        this.policiesAlertsService.addUserSuccess()
      })
      .catch((error) => {
        this.$log.error(error);
        this.policiesAlertsService.addUserError(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  deleteUser(policyId, userId, orgId, siteId) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.policiesRequestsService.deleteUser(policyId, userId, orgId, siteId)
      .then(() => {
        deferred.resolve();
        this.policiesAlertsService.removeUserSuccess()
      })
      .catch((error) => {
        this.$log.error(error);
        this.policiesAlertsService.removeUserError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }


  //////////////////////////////////////// Roles Groups ////////////////////////////////////////
  getAllRolesGroups(policyId, orgId?: string, siteId?: string): IPromise<any[]> {
    const deferred = this.$q.defer<any[]>();

    this.bringozLoaderService.on();
    this.policiesRequestsService.getAllRolesGroups(policyId, orgId, siteId)
      .then((rolesGroups) => {
        deferred.resolve(rolesGroups as any[]);
      })
      .catch((error) => {
        this.policiesAlertsService.getAllRolesGroups();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
    return deferred.promise
  }

  createRolesGroup(group, policyId, orgId?: string, siteId?: string): IPromise<any[]> {
    const deferred = this.$q.defer<any[]>();

    this.bringozLoaderService.on();
    this.policiesRequestsService.createRolesGroup(group, policyId, orgId, siteId)
      .then((rolesGroup) => {
        this.policiesAlertsService.createRolesGroupSuccess();
        deferred.resolve(rolesGroup as any[]);
      })
      .catch((error) => {
        this.policiesAlertsService.createRolesGroupError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
    return deferred.promise
  }

  deleteRolesGroup(groupId, policyId, orgId?: string, siteId?: string): IPromise<any[]> {
    const deferred = this.$q.defer<any[]>();

    this.bringozLoaderService.on();
    this.policiesRequestsService.deleteRolesGroup(groupId, policyId, orgId, siteId)
      .then((rolesGroup) => {
        this.policiesAlertsService.deleteRolesGroupSuccess();
        deferred.resolve(rolesGroup as any[]);
      })
      .catch((error) => {
        this.policiesAlertsService.deleteRolesGroupError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
    return deferred.promise
  }

  editRolesGroup(payload, groupId, policyId, orgId?: string, siteId?: string): IPromise<any[]> {
    const deferred = this.$q.defer<any[]>();

    this.bringozLoaderService.on();
    this.policiesRequestsService.editRolesGroup(payload, groupId, policyId, orgId, siteId)
      .then((rolesGroup) => {
        this.policiesAlertsService.editRolesGroupSuccess();
        deferred.resolve(rolesGroup as any[]);
      })
      .catch((error) => {
        this.policiesAlertsService.editRolesGroupError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
    return deferred.promise
  }

  addMembersToGroup(groupId, policyId, userIds, orgId?: string, siteId?: string): IPromise<any> {
    const deferred = this.$q.defer<any>();

    this.bringozLoaderService.on();
    this.policiesRequestsService.addMembersToGroup(groupId, policyId, userIds, orgId, siteId)
      .then((rolesGroup) => {
        this.policiesAlertsService.addMembersToGroupSuccess();
        deferred.resolve(rolesGroup as any[]);
      })
      .catch((error) => {
        this.policiesAlertsService.addMembersToGroupError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
    return deferred.promise
  }

  removeMembersFromGroup(groupId, policyId, userIds, orgId?: string, siteId?: string): IPromise<any> {
    const deferred = this.$q.defer<any>();

    this.bringozLoaderService.on();
    this.policiesRequestsService.removeMembersFromGroup(groupId, policyId, userIds, orgId, siteId)
      .then((rolesGroup) => {
        this.policiesAlertsService.removeMembersFromGroupSuccess();
        deferred.resolve(rolesGroup as any[]);
      })
      .catch((error) => {
        this.policiesAlertsService.removeMembersFromGroupError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
    return deferred.promise
  }

}

angular
  .module('bringoz.admin.policies')
  .service('policiesService', PoliciesService);
